import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  activeFilterCount as countActiveFilters,
  type ListControlsState,
  type SortDir,
  type SortState,
  type ViewMode,
} from '@/_shared/lib/list-controls';

const EMPTY_FILTER_KEYS: readonly string[] = [];
const STORAGE_PREFIX = 'list-controls:';

/** Cross-session preferences we remember per list. Filters are intentionally NOT persisted (sticky
 *  filters silently hide data across sessions — a known trap); they stay URL-only / session-scoped. */
interface StoredPrefs {
  view?: ViewMode;
  /** Serialized as `"key.dir"`, matching the URL form. */
  sort?: string;
}

function readPrefs(key: string): StoredPrefs {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writePrefs(key: string, prefs: StoredPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(prefs));
  } catch {
    /* ignore quota / serialization errors */
  }
}

export interface UseListControlsOptions<SortKey extends string, FilterKey extends string> {
  /** The valid sort keys; a URL value outside this set falls back to `defaultSort`. */
  sortKeys: readonly SortKey[];
  defaultSort: SortState<SortKey>;
  /** Initial view when neither the URL nor a saved preference pins one (default `'card'`). */
  defaultView?: ViewMode;
  /** The filter ids this list supports (used to read/clear their URL params). */
  filterKeys?: readonly FilterKey[];
  /**
   * Allowed values per filter key. When given, a URL value outside the set is ignored (dropped, not
   * treated as an active filter) — same sanitisation `parseSort` does for sort keys, so a hand-edited
   * `?f_status=bogus` can't render a phantom chip that matches zero rows.
   */
  filterOptions?: Partial<Record<FilterKey, readonly string[]>>;
  /** Prefix for URL param names, so two lists sharing a route don't collide (default `''`). */
  paramPrefix?: string;
  /**
   * When set, the user's `view` + `sort` are remembered across sessions under this key
   * (`localStorage["list-controls:<persistKey>"]`). Filters are never persisted. Omit for no memory.
   */
  persistKey?: string;
}

export interface UseListControlsReturn<SortKey extends string, FilterKey extends string> extends ListControlsState<
  SortKey,
  FilterKey
> {
  setQuery: (query: string) => void;
  setView: (view: ViewMode) => void;
  setSort: (sort: SortState<SortKey>) => void;
  setFilter: (key: FilterKey, value: string | null) => void;
  clearFilters: () => void;
  /** Reset every filter AND the sort (back to `defaultSort`) in a single URL write. */
  clearAll: () => void;
  activeFilterCount: number;
}

function parseSort<SortKey extends string>(
  raw: string | null | undefined,
  keys: readonly SortKey[],
  fallback: SortState<SortKey>,
): SortState<SortKey> {
  if (!raw) return fallback;
  const [key, dir] = raw.split('.');
  if (!keys.includes(key as SortKey)) return fallback;
  const nextDir: SortDir = dir === 'asc' || dir === 'desc' ? dir : fallback.dir;
  return { key: key as SortKey, dir: nextDir };
}

/**
 * Owns a list page's control state, split by what each piece *is*:
 *   - `filters` = WHICH items you're looking at → the shareable/deep-linkable part, lives ONLY in the URL
 *     (written `{ replace: true }` so a tweak isn't a Back-button trap). Never persisted across sessions
 *     (sticky filters silently hide data — a known trap).
 *   - `view` + `sort` = HOW you like to look at them → personal preferences, saved to `localStorage` (per
 *     `persistKey`) and NOT in the URL, so a returning user keeps them but links stay clean.
 *   - `query` = ephemeral local state (search-as-you-type shouldn't spam history or persist).
 */
export function useListControls<SortKey extends string, FilterKey extends string = never>(
  options: UseListControlsOptions<SortKey, FilterKey>,
): UseListControlsReturn<SortKey, FilterKey> {
  const { sortKeys, defaultSort, defaultView = 'card', paramPrefix = '', persistKey, filterOptions } = options;
  const filterKeys = (options.filterKeys ?? EMPTY_FILTER_KEYS) as readonly FilterKey[];
  // A stable primitive key over the filter-id set, so the `filters` memo doesn't churn just because a
  // caller passed a fresh `filterKeys` array literal each render (its contents, not identity, matter).
  const filterKeysKey = filterKeys.join('\u0000');

  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [stored, setStored] = useState<StoredPrefs>(() => (persistKey ? readPrefs(persistKey) : {}));

  // Keep localStorage in sync with the saved-prefs state (runs on mount + whenever a pref changes).
  useEffect(() => {
    if (persistKey) writePrefs(persistKey, stored);
  }, [persistKey, stored]);

  const updatePrefs = useCallback((patch: StoredPrefs) => {
    setStored((prev) => {
      const next: StoredPrefs = { ...prev, ...patch };
      if (next.view === undefined) delete next.view;
      if (next.sort === undefined) delete next.sort;
      return next;
    });
  }, []);

  const name = useCallback((key: string) => `${paramPrefix}${key}`, [paramPrefix]);

  // view + sort come from saved prefs only (localStorage) — never the URL. Memoized so `sort` keeps a
  // stable identity across renders (once a non-default sort is stored, `parseSort` returns a fresh object
  // each call), which is what downstream `useMemo`s that derive the visible list depend on.
  const storedView = stored.view === 'card' || stored.view === 'list' ? stored.view : undefined;
  const view: ViewMode = storedView ?? defaultView;
  const sort = useMemo(() => parseSort(stored.sort, sortKeys, defaultSort), [stored.sort, sortKeys, defaultSort]);

  const paramsKey = searchParams.toString();
  const filters = useMemo<Partial<Record<FilterKey, string | null>>>(() => {
    const next: Partial<Record<FilterKey, string | null>> = {};
    for (const key of filterKeys) {
      const value = searchParams.get(name(`f_${key}`));
      if (value == null || value === '') continue;
      // Ignore a URL value that isn't a known option for this filter (e.g. a hand-edited `?f_status=bogus`),
      // so it can't become an active filter that matches nothing and shows a misleading chip.
      const allowed = filterOptions?.[key];
      if (allowed && !allowed.includes(value)) continue;
      next[key] = value;
    }
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- paramsKey captures the searchParams content; name/filterOptions are stable; filterKeysKey captures the filter-id set by value.
  }, [paramsKey, name, filterKeysKey, filterOptions]);

  const mutate = useCallback(
    (apply: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          apply(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setView = useCallback((next: ViewMode) => updatePrefs({ view: next }), [updatePrefs]);

  const setSort = useCallback(
    (next: SortState<SortKey>) => updatePrefs({ sort: `${next.key}.${next.dir}` }),
    [updatePrefs],
  );

  const setFilter = useCallback(
    (key: FilterKey, value: string | null) =>
      mutate((p) => {
        const paramName = name(`f_${key}`);
        if (value == null || value === '') p.delete(paramName);
        else p.set(paramName, value);
      }),
    [mutate, name],
  );

  const clearFilters = useCallback(
    () => mutate((p) => filterKeys.forEach((key) => p.delete(name(`f_${key}`)))),
    [mutate, name, filterKeys],
  );

  // Reset the search query, the filters (URL), and the saved sort (localStorage) together, so "Clear all"
  // truly returns to a clean list — otherwise it removes every chip while an active search silently keeps
  // the list narrowed with nothing left to explain why.
  const clearAll = useCallback(() => {
    setQuery('');
    updatePrefs({ sort: undefined });
    mutate((p) => filterKeys.forEach((key) => p.delete(name(`f_${key}`))));
  }, [mutate, name, filterKeys, updatePrefs]);

  return {
    query,
    view,
    sort,
    filters,
    setQuery,
    setView,
    setSort,
    setFilter,
    clearFilters,
    clearAll,
    activeFilterCount: countActiveFilters(filters),
  };
}
